import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getGroups, deleteGroup } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput"; // المكون المركزي
import ConfirmModal from "../components/UI/ConfirmModal"; // المودال المركزي
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [groupsPerPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchGroups = async () => {
    try {
      const data = await getGroups(token);
      setGroups(data || []);
    } catch (err) { 
      console.error(err); 
    }
  };

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // منطق البحث والفرز المدمج (useMemo للأداء العالي)
  const sortedAndFilteredGroups = useMemo(() => {
    let result = [...groups].filter(g => 
      g.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [groups, searchTerm, sortConfig]);

  const currentGroups = sortedAndFilteredGroups.slice((currentPage - 1) * groupsPerPage, currentPage * groupsPerPage);
  const totalPages = Math.ceil(sortedAndFilteredGroups.length / groupsPerPage) || 1;
  const totalItems = Math.ceil(sortedAndFilteredGroups.length ) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  // فتح المودال للحذف
  const handleDeleteClick = (id) => {
    setModalConfig({ isOpen: true, id });
  };

  // تنفيذ الحذف الفعلي مع معالجة الأخطاء (مثل وجود مستخدمين في الجروب)
  const confirmDelete = async () => {
    try {
      await deleteGroup(token, modalConfig.id);
      notify.success("Group deleted successfully");
      fetchGroups();
    } catch (err) {
      // هنا نستخدم نفس منطق الـ catch الذي ضبطناه في الـ Permissions
      const serverData = err.data;
      if (serverData?.users && serverData.users.length > 0) {
        const userNames = serverData.users.join(", ");
        notify.error(`Denied: This group contains users [ ${userNames} ]`, { duration: 5000 });
      } else {
        notify.error(err.message || "Failed to delete group");
      }
    } finally {
      setModalConfig({ isOpen: false, id: null });
    }
  };

  // تعريف الأعمدة للجدول المركزي
  const columns = [
    { key: "id", label: "ID", className: "text-gray-400 font-mono" },
    { 
      key: "name", 
      label: "Group Name", 
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-[10px]">
            {row.name.substring(0, 2).toUpperCase()}
          </div>
          {row.name}
        </div>
      )
    }
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      
      {/* المودال المركزي */}
      <ConfirmModal 
        isOpen={modalConfig.isOpen}
        title="Delete Group"
        message="Are you sure? This will remove the group and its linked permissions."
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
      />

      {/* Header Section */}
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Groups size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Groups</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">Manage roles and access control</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          {/* البحث المركزي */}
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search groups..."
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-group")} className="w-full sm:w-auto shrink-0">
            Add Group
          </Button>
        </div>
      </PageHeader>
      <DataTable 
  columns={columns}
  data={currentGroups}
  sortConfig={sortConfig}
  onSort={requestSort}
  renderActions={(row, isMobile) => (
    <>
      <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-group/${row.id}`)}>{isMobile && " Edit"}</Button>
      <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && " Delete"}</Button>
    </>
  )}
  mobileRender={(row) => (
    <div className="flex justify-between items-center">
      <span className="text-[10px] text-gray-400 font-mono">ID: #{row.id}</span>
      <h3 className="font-bold text-gray-800">{row.name}</h3>
    </div>
  )}

  paginationConfig={{
    currentPage: currentPage,
    totalPages: totalPages,
    onPageChange: (newPage) => setCurrentPage(newPage),
    totalItems
  }}
/>
    </div>
  );
}